import React, { memo, useState, useCallback } from "react";
import { Popover } from "antd";
import ReactionPicker from "../ReactionPicker";
import { useAddReaction } from "../../hooks/mutations/useAddReaction";
import styles from "./ReactionDisplay.module.scss";

interface ReactionDisplayProps {
  messageId: string;
  roomId: string;
  totalReactions: number;
  myReaction: number | null;
  otherReactions?: number[];
  onShowDetails?: () => void;
}

// Reaction icons giống module cũ
const REACTION_ICONS = [
  "/icons/favorite.png.png",
  "/icons/love.png.png",
  "/icons/care.png.png",
  "/icons/haha.png.png",
  "/icons/wow.png.png",
  "/icons/sad.png.png",
  "/icons/like.png.png",
  "/icons/done.png.png",
];

export const ReactionDisplay: React.FC<ReactionDisplayProps> = ({
  messageId,
  roomId,
  totalReactions,
  myReaction,
  otherReactions = [],
  onShowDetails,
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const { mutate: addReaction } = useAddReaction(roomId);

  const handleReactionSelect = useCallback(
    (reactionIndex: number) => {
      addReaction(
        {
          MessageId: messageId,
          roomId,
          Reaction: reactionIndex,
        },
        {
          onSuccess: () => {
            setShowPicker(false);
          },
        },
      );
    },
    [messageId, roomId, addReaction],
  );

  const getReactionIcon = useCallback((reactionType: number | null) => {
    if (reactionType === null || reactionType === undefined || reactionType === 0) {
      return REACTION_ICONS[0]; // Default icon
    }
    return REACTION_ICONS[reactionType] || REACTION_ICONS[0];
  }, []);

  // Chỉ hiển thị khi có data thực sự và có reactions từ người khác
  const hasOthersReactions = otherReactions.length > 0;

  const handleOthersClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent popover from opening
      if (onShowDetails) {
        onShowDetails();
      }
    },
    [onShowDetails],
  );

  const handleMyReactionClick = useCallback((e: React.MouseEvent) => {
    // This will be handled by Popover
  }, []);

  return (
    <div className={styles.reactionContainer}>
      {/* Others' Reactions - Show only if others have reacted */}
      {hasOthersReactions && (
        <div className={styles.othersReactions} onClick={handleOthersClick}>
          <div className={styles.reactionIcons}>
            {otherReactions.slice(0, 3).map((reaction, index) => (
              <img
                key={`${messageId}-reaction-${index}`}
                src={REACTION_ICONS[reaction]}
                alt="reaction"
                className={styles.reactionIcon}
                style={{ zIndex: 3 - index }}
              />
            ))}
          </div>
          <span className={styles.count}>{totalReactions > 3 ? `${totalReactions}+` : totalReactions}</span>
        </div>
      )}

      {/* My Reaction - Always show */}
      <Popover
        content={<ReactionPicker onReactionSelect={handleReactionSelect} currentReaction={myReaction} />}
        trigger="click"
        open={showPicker}
        onOpenChange={setShowPicker}
        placement="topRight"
        overlayClassName="reaction-popover-custom"
      >
        <div className={styles.myReaction} onClick={handleMyReactionClick}>
          <img src={getReactionIcon(myReaction)} alt="my reaction" className={styles.myReactionIcon} />
        </div>
      </Popover>
    </div>
  );
};

export default memo(ReactionDisplay);
